import ImageComponent from '@/ui-library/atoms/ImageComponent.vue'
import { normalizeImage } from '@/ui-library/types/image.types'
import { mapActions } from 'vuex'
import httpClient from '../api/httpClient'
import { caseOf, filter, map } from '../lib'
import { isEmptyValue } from '../utils/trafficSourceCostsUtils'
import { vatFormats } from '../utils/vat'
import Checkbox from './Checkbox'
import DropDown from './DropDown'
import ErrorBlock from './ErrorBlock'
import FormStep from './form-wizard/FormStep'
import FormWizard from './form-wizard/FormWizard'
import Help from './HelpText'
import Input from './Input'
import LocaleDropDown from './LocaleDropDown'
import MultiInputs from './metapic/inputs/MultiInputs/MultiInputs'
import TrafficSources from './TrafficSources'

const _updateField = (context, field) => (value) => {
  if (context.validationStatus !== '') {
    context.clearValidationStatus()
  }

  context.updateField({ field, value })
}

const updateUtmTag = (value, context, tag) => {
  context.setUtmTags({ tag, data: { value } })
}

export default {
  render() {
    return (
      <div class="card">
        <FormWizard ref="wizard" onComplete={this.onComplete} onChangeStep={this.context.updateCurrentStep}>
          <FormStep title="Set store information" beforeChange={this.validateFirstTab}>
            <div>
              <Input
                id="edit-store-name"
                required
                disabled={this.store.autoupdate}
                label="Name"
                placeholder="E.g. Nelly or NA-KD"
                value={this.store.name}
                helper-topic="Help name"
                update={_updateField(this.context, 'name')}
              >
                <span>
                  Name of the store. <br />
                  Use the brand name and not like NELLY SE <br /> or NELLY.com.
                </span>
              </Input>
              {this.firstTabIsDirty && !this.store.name && <p class="error-form-text">Please enter a store name</p>}
            </div>

            <DropDown
              id="edit-store-provider"
              label="Provider"
              disabled={this.store.autoupdate}
              value={this.store.provider}
              update={_updateField(this.context, 'provider')}
              items={this.storeProviders.map((storeProvider) => {
                return {
                  value: storeProvider.key,
                  label: storeProvider.key,
                }
              })}
              default={{ value: 'Choose provider', unselectable: true }}
              required
            />
            {this.firstTabIsDirty && !this.store.provider && <p class="error-form-text">Please enter a provider</p>}
            {this.store.provider && this.storeProviders.length > 0 && (
              <div>
                <DropDown
                  id="edit-store-provider-account"
                  label="Provider account"
                  disabled={this.store.autoupdate}
                  value={this.store.providerAccountID}
                  update={_updateField(this.context, 'providerAccountID')}
                  items={this.storeProviders
                    .find((storeProvider) => {
                      return this.store.provider == storeProvider.key
                    })
                    .accounts.map((account) => {
                      return {
                        value: account.id,
                        label: account.key,
                      }
                    })}
                  default={{ value: 'Choose provider', unselectable: true }}
                  required
                />
                {this.firstTabIsDirty && !this.store.providerAccountID && <p class="error-form-text">Please enter a provider account</p>}
              </div>
            )}
            <Input
              id="edit-programId"
              disabled={this.store.autoupdate}
              label="Program id"
              placeholder="E.g. 1232"
              value={this.store.programID}
              update={(value) =>
                (Number.isInteger(parseInt(value)) || value === '') && this.context.updateField({ field: 'programID', value })
              }
            />

            {this.showUtmTagCheckBox() && (
              <Checkbox
                className="utm-tags"
                label="UTM TAGs"
                disabled={this.store.autoupdate}
                checked={this.context.showUtmTags}
                update={() => this.context.setShowUtmTags(!this.context.showUtmTags)}
              />
            )}

            {this.showUtmTagCheckBox() &&
              this.context.showUtmTags &&
              Object.values(this.context.utmTags).map((utmTag) => (
                <Input
                  key={`input${utmTag.label}`}
                  id={`edit-${utmTag.label}`}
                  disabled={this.store.autoupdate}
                  label={utmTag.label}
                  placeholder={utmTag.example}
                  value={utmTag.value}
                  update={(value) => {
                    updateUtmTag(value, this.context, utmTag.name)
                    this.rebuildUrl()
                  }}
                />
              ))}

            <div>
              <Input
                id="edit-store-feed-url"
                label="Url"
                disabled={this.store.autoupdate}
                placeholder={`E.g. ${this.context.urlPlaceholder}`}
                value={this.store.url}
                required
                update={_updateField(this.context, 'url')}
              >
                <span>
                  URL is the deeplinking URL scheme our tracking partner uses.
                  <br />
                  There you replace the url with :TARGET_URL.
                  <br />
                  Example: https://clk.tradedoubler.com/click?p(70361)a(2978484)url(:TARGET_URL)
                </span>
              </Input>
              {this.firstTabIsDirty && !this.store.url && <p class="error-form-text">Please enter a url</p>}
            </div>

            <DropDown
              id="edit-store-language"
              label="Language"
              disabled={this.store.autoupdate}
              value={this.store.language}
              update={_updateField(this.context, 'language')}
              items={[
                { value: 'da', label: 'Danish' },
                { value: 'de', label: 'Deutsch' },
                { value: 'en', label: 'English' },
                { value: 'fi', label: 'Finnish' },
                { value: 'it', label: 'Italian' },
                { value: 'no', label: 'Norwegian' },
                { value: 'pl', label: 'Polish' },
                { value: 'sv', label: 'Swedish' },
                { value: 'es', label: 'Spanish' },
                { value: 'fr', label: 'France' },
                { value: 'cs', label: 'Czech' },
                { value: 'nl', label: 'Dutch' },
                { value: 'ru', label: 'Russian' },
                { value: 'sk', label: 'Slovak' },
                { value: 'ro', label: 'Romanian' },
              ]}
              default={{ value: 'Choose language', unselectable: true }}
            />

            <LocaleDropDown
              id="store-locale"
              value={this.store.locale}
              disabled={this.store.autoupdate}
              update={_updateField(this.context, 'locale')}
              default={{ value: 'Choose locale', unselectable: true }}
              required
            />
            {this.firstTabIsDirty && !this.store.locale && <p class="error-form-text">Please enter a locale</p>}
          </FormStep>
          <FormStep title="Set payment" beforeChange={this.validateSecondTab}>
            <DropDown
              id="edit-store-currency"
              label="Currency"
              disabled={this.store.autoupdate}
              value={this.store.currency}
              update={_updateField(this.context, 'currency')}
              items={[
                { value: 'DKK', label: 'DKK' },
                { value: 'EUR', label: 'EUR' },
                { value: 'NOK', label: 'NOK' },
                { value: 'PLN', label: 'PLN' },
                { value: 'SEK', label: 'SEK' },
                { value: 'GBP', label: 'GBP' },
                { value: 'AUD', label: 'AUD' },
                { value: 'USD', label: 'USD' },
              ]}
              default={{ value: 'Choose currency', unselectable: true }}
              required
            />
            {this.secondTabIsDirty && !this.store.currency && <p class="error-form-text">Please enter a currency</p>}

            <label>
              Payment *:{' '}
              <Help>You need to fill in at least one of the options for payment type. CPA (Cost Per Action) or CPC (Cost Per Click).</Help>
            </label>
            <div>
              <TrafficSources
                isNew={this.isNewStore}
                trafficSourceCosts={this.store.trafficSourceCosts}
                currency={this.store.currency}
                update={_updateField(this.context, 'trafficSourceCosts')}
              />
              {this.hasUnfinishedTrafficSourceCosts && this.secondTabIsDirty && (
                <p className="error-form-text">Please fill in all traffic sources or delete unnecessary ones.</p>
              )}
            </div>
          </FormStep>
          <FormStep title="Invoicing" beforeChange={this.validateInvoiceTab}>
            <h2>Invoicing:</h2>
            <div>
              <DropDown
                id="edit-store-payment_type"
                label="Payment Type"
                disabled={this.store.autoupdate}
                value={this.store.paymentType}
                update={_updateField(this.context, 'paymentType')}
                items={[
                  { value: 'invoice', label: 'Invoice' },
                  { value: 'stripe', label: 'Stripe' },
                  { value: 'other', label: 'Other' },
                ]}
                default={{ value: 'Choose payment type', unselectable: true }}
                required
              />
              {this.thirdTabIsDirty && !this.store.paymentType && <p class="error-form-text">Please enter a payment type</p>}
            </div>
            <Input
              id="minimum-return-rate"
              label="Minimum return rate"
              disabled={this.store.autoupdate}
              placeholder="E.g. 20.3"
              value={this.store.minimumReturnRate}
              isPercentage
              update={_updateField(this.context, 'minimumReturnRate')}
            />
            {this.store.paymentType !== 'other' && (
              <div>
                <Input
                  id="overridePayment"
                  label="Override Payment (%)"
                  placeholder="E.g. 20"
                  isPercentage
                  value={this.store.overridePaymentPercentage}
                  update={_updateField(this.context, 'overridePaymentPercentage')}
                />
                <h2>Invoice information</h2>
                <Input
                  id="reference"
                  label="Reference"
                  placeholder="E.g. John Robert"
                  value={this.store.invoiceConfig?.reference}
                  update={(value) => (this.store.invoiceConfig.reference = value)}
                />

                <MultiInputs
                  addButtonTitle="Add Email"
                  max={3}
                  input={this.emailConfigInput}
                  inputs={this.emailConfigInputs}
                  inputValues={this.store.invoiceConfig?.emails}
                  onAdd={() => this.emailConfigInputs.push(this.emailConfigInput)}
                  onRemove={(index) => {
                    this.emailConfigInputs.splice(index, 1)
                    this.store.invoiceConfig.emails.splice(index, 1)
                  }}
                />

                <Input
                  id="vat_number"
                  label="Vat Number"
                  placeholder={`E.g. ${this.vatFormat}`}
                  value={this.store.invoiceConfig?.vat_number}
                  update={(value) => (this.store.invoiceConfig.vat_number = value)}
                  required
                  onChange={this.validateVat}
                  disabled={this.store.isVatCheckLoading}
                  isLoading={this.store.isVatCheckLoading}
                />
                {this.thirdTabIsDirty && !this.store.invoiceConfig?.vat_number && <p class="error-form-text">Please enter VAT number</p>}
                {this.thirdTabIsDirty && this.isVatDirty && !this.isVatCheckValid && this.store.invoiceConfig?.vat_number && (
                  <p className="error-form-text">VAT number is invalid</p>
                )}

                <Input
                  id="company_name"
                  label="Company Name"
                  placeholder="E.g. Metapic Ab"
                  value={this.store.invoiceConfig?.company_name}
                  update={(value) => (this.store.invoiceConfig.company_name = value)}
                />

                <div>
                  <Input
                    id="due_days"
                    type="number"
                    label="Due days"
                    placeholder="0-90 days"
                    value={this.store.invoiceConfig?.due_days}
                    update={(value) => (this.store.invoiceConfig.due_days = value)}
                  />
                  {this.thirdTabIsDirty && !this.isDueDaysValid(this.store.invoiceConfig.due_days) && (
                    <p class="error-form-text">Please enter a number of due days between 0 and 90</p>
                  )}
                </div>
                <h2>Address</h2>
                <div className="address-container">
                  {[0, 1, 2].map((i) => (
                    <Input
                      key={i}
                      id={`address_name${i}`}
                      value={this.store.invoiceConfig?.invoicing_address[i] ? this.store.invoiceConfig.invoicing_address[i] : ''}
                      onChange={(value) => {
                        this.store.invoiceConfig.invoicing_address[i] = value
                      }}
                    />
                  ))}
                </div>
                <Checkbox
                  checked={this.store.dontCountActions}
                  update={_updateField(this.context, 'dontCountActions')}
                  label="Don't count CPA/CPC"
                />
              </div>
            )}
            <Checkbox
              checked={this.store.ignoreRemoteEarnings}
              update={_updateField(this.context, 'ignoreRemoteEarnings')}
              label="Ignore remote earnings"
            />
          </FormStep>
          <FormStep title="Set visibility settings" beforeChange={() => (this.thirdTabIsDirty = true)}>
            <h2>Set visibility settings</h2>
            {!this.isNewStore && (
              <div>
                <p>Store Status:</p>
                <DropDown
                  id="edit-store-status"
                  label="Store Status"
                  value={this.store.status}
                  update={_updateField(this.context, 'status')}
                  items={[
                    { value: 0, label: 'Unset' },
                    { value: 1, label: 'Approved' },
                    { value: 2, label: 'Denied' },
                  ]}
                  required
                />
              </div>
            )}
            {this.thirdTabIsDirty && !this.store.status && <p class="error-form-text">Please enter a store status</p>}
            <p>Store Groups:</p>
            <div class="input-grid">
              {caseOf(this.context.groups, {
                Done: (groups) =>
                  map(groups, (group) => (
                    <Checkbox
                      key={group.id}
                      id={group.id}
                      checked={this.context.isStoreInGroup(group.id)}
                      update={(checked) =>
                        this.context.toggleGroupConnection({
                          group,
                          checked,
                        })
                      }
                      label={group.name}
                    />
                  )),
                _: () => <div class="spinner" />,
              })}
            </div>
            <p style={this.isNewStore ? { display: 'none' } : {}}>Recommended:</p>
            <div style={this.isNewStore ? { display: 'none' } : {}} class="input-grid">
              {caseOf(this.context.groups, {
                Done: (groups) => {
                  const filterGroups = filter(groups, (group) => this.context.isStoreInGroup(group.id) === true)
                  return map(filterGroups, (group) => (
                    <Checkbox
                      key={group.id}
                      id={group.key}
                      checked={this.context.isGroupInTopList(group)}
                      update={(checked) =>
                        this.context.toggleTopList({
                          group,
                          checked,
                        })
                      }
                      label={group.key}
                    />
                  ))
                },
                _: () => <div className="spinner" />,
              })}
            </div>
            <p>Categories:</p>
            <div class="input-grid">
              {this.storeCategories.map((storeCategory) => (
                <Checkbox
                  key={storeCategory.id}
                  id={storeCategory.id}
                  checked={this.context.isStoreInCategory(storeCategory.id)}
                  update={(checked) =>
                    this.context.toggleCategoryConnection({
                      storeCategory,
                      checked,
                    })
                  }
                  label={storeCategory.name}
                />
              ))}
            </div>
            <p>
              Visibility for users:
              <Help>
                If the influencer should be able to see and deeplink the store.
                <br /> This can be hidden if you only want some users to see the
                <br /> store you can add a offer that shows it.
              </Help>
            </p>
            <DropDown
              value={this.store.hidden}
              update={_updateField(this.context, 'hidden')}
              items={[
                { value: 0, label: 'Show For All Users' },
                { value: 1, label: 'Show For Invited Users' },
                { value: 2, label: 'Show For Verified Users' },
                { value: 3, label: 'Deeplink only' },
              ]}
            />
            <p>Settings:</p>
            <Checkbox
              checked={this.store.showOrders}
              update={_updateField(this.context, 'showOrders')}
              label="Display orders on dashboard for influencers"
            />
            <Checkbox
              checked={this.store.ignoreRule6}
              update={_updateField(this.context, 'ignoreRule6')}
              label="Ignore rule that the user need to be the same country as store"
            />
            <Checkbox
              checked={this.store.advertiserUseCPA}
              update={_updateField(this.context, 'advertiserUseCPA')}
              label="Advertiser can use CPA"
            />
            <Checkbox
              checked={this.store.autoAcceptInstagramStories}
              update={_updateField(this.context, 'autoAcceptInstagramStories')}
              label="Auto accept instagram stories"
            />
            <label>Store Logo:</label>
            <el-upload action="" accept="image/jpeg,image/png" show-file-list={false} auto-upload={false} onChange={this.handleLogoUpload}>
              {this.storeLogo ? (
                <div class="store-image-container">
                  <ImageComponent image={this.storeLogo} width={200} height={100} />
                </div>
              ) : (
                <div class="store-image-container empty">
                  <empty-image-icon />
                  <p>Upload image(280x100)</p>
                </div>
              )}
            </el-upload>
            {this.isExceedImgSize && <p class="error">Logo picture size can not exceed 0.0375 MB!</p>}
            {caseOf(this.context.status, {
              Idle: () => this.context.validationStatus !== '' && <div class="error">{this.context.validationStatus}</div>,
              Processing: () => <div class="spinner" />,
              Saved: () => <div class="success">Store was saved successfully</div>,
              Updated: () => <div class="success">Store was updated successfully</div>,
              Failed: () => <ErrorBlock error={this.context.errorMessage} />,
            })}
          </FormStep>
        </FormWizard>
        {this.isNewStore && (
          <div class="reset-btn-wizard-div">
            <button class="reset-btn-wizard" onClick={this.onReset}>
              Reset
            </button>
          </div>
        )}
      </div>
    )
  },

  props: {
    context: Object,
    store: Object,
    storeCategories: Array,
    storeProviders: Array,
    action: Function,
    isNewStore: Boolean,
  },
  data() {
    return {
      firstTabIsDirty: false,
      secondTabIsDirty: false,
      thirdTabIsDirty: false,
      isExceedImgSize: false,
      logoSrc: null,
      emailConfigInputs: [],
      defaultStoreUrl: '',
      isVatCheckValid: false,
    }
  },
  created() {
    this.defaultStoreUrl = this.store.url
    this.context.updateFeedOrUrl(null)
    this.context.setEmptyPaymentTypes()
    this.context.initStore(this.isNewStore)
    this.fetchTrafficSources()
    !this.isNewStore && this.setConfigEmailInputs
  },
  components: {
    FormWizard,
    FormStep,
    TrafficSources,
  },
  computed: {
    hasUnfinishedTrafficSourceCosts() {
      const isEditingMode = !this.isNewStore

      return (
        isEditingMode &&
        this.store.trafficSourceCosts.some((item) => {
          const noSourceIndexes = item.sourceIndexes.length === 0
          const hasRateCpc = !isEmptyValue(item.rateCpc)
          const hasRateCpa = !isEmptyValue(item.rateCpa)

          return (noSourceIndexes && (hasRateCpc || hasRateCpa)) || (!noSourceIndexes && !(hasRateCpc || hasRateCpa))
        })
      )
    },
    isEmptyPaymentValues() {
      return this.store.trafficSourceCosts.some((item) => !item.rate || !item.type || item.source == null)
    },
    isGeneralSourceIncluded() {
      return this.store.trafficSourceCosts.some((item) => item.source === 0)
    },
    storeLogo() {
      if (this.logoSrc) return { url: this.logoSrc }
      if (this.store.logo) return normalizeImage(this.store.logo)
      return { url: this.context.storeLogo || this.store.logoUrl }
    },
    emailConfigInput() {
      const emails = this.store.invoiceConfig?.emails
      return {
        id: 'invoice-email',
        label: 'Invoice Email',
        placeholder: 'E.g John@gmail.com',
        type: 'email',
        onChange: (value, index) => (emails[index] = value),
      }
    },
    setConfigEmailInputs() {
      const emailConfigInput = this.emailConfigInput
      const emails = this.store.invoiceConfig.emails
      if (!emails) return
      this.emailConfigInputs = emails.map((email) => {
        return { ...emailConfigInput, value: email }
      })
    },
    vatFormat() {
      return vatFormats[this.store.locale]
    },
  },
  methods: {
    ...mapActions('trafficSources', ['fetchTrafficSources']),
    validateFirstTab() {
      this.firstTabIsDirty = true
      return Boolean(this.store.name && this.store.provider && this.store.providerAccountID && this.store.locale)
    },
    validateSecondTab() {
      this.secondTabIsDirty = true
      return this.store.currency && !this.hasUnfinishedTrafficSourceCosts
    },

    validateInvoiceTab() {
      this.thirdTabIsDirty = true
      if (!this.store.paymentType) {
        return false
      }

      // We only validate this tab if `Invoice` type was selected
      if (this.store.paymentType === 'invoice' && this.isVatDirty && !this.isVatCheckValid) {
        return false
      }

      if (this.store.paymentType !== 'other' && !this.isDueDaysValid(this.store.invoiceConfig.due_days)) {
        return false
      }

      return true
    },

    // A function that will do the additional validation on backend side
    async validateVatWithApi(value) {
      this.store.isVatCheckLoading = true

      try {
        const { data } = await httpClient.get('/validate-vat', { params: { vat_no: value } })
        this.isVatCheckValid = data.is_valid
      } catch (e) {
        throw new Error('Could not fetch VAT validation')
      } finally {
        this.store.isVatCheckLoading = false
      }
    },

    async validateVat(value) {
      this.isVatDirty = true
      if (!(value && value.trim())) return false

      // Some countries do not require VAT, but it still can be filled in, we do not need to validate
      // those on backend side
      const isNotVatCountry = ['UA', 'GB', 'NO'].includes(this.store.locale)

      // If a country requires a VAT, we need to do a check on backend side
      if (!isNotVatCountry) {
        await this.validateVatWithApi(value)
        // Otherwise we just set it to true
      } else this.isVatCheckValid = true
    },

    onComplete() {
      if (!this.context.invalidDomains.indexList.length) {
        this.context.isFormValid().then((valid) => {
          if (valid) {
            this.action()
          }
        })
      }
    },
    handleLogoUpload(res) {
      const file = res.raw
      const maxLogoFileSizeInBytes = 300000
      this.isExceedImgSize = file.size > maxLogoFileSizeInBytes
      if (!this.isExceedImgSize) {
        this.logoSrc = URL.createObjectURL(file)
        this.context.setLogoFile(this.normalizeLogoData(file))
      }
    },
    normalizeLogoData(file) {
      const formData = new FormData()
      formData.append('file', file)
      return formData
    },
    onReset() {
      this.$refs.wizard.changeStep(0)
      const input = this.$refs.selectLogoInput
      if (input) {
        input.type = 'text'
        input.type = 'file'
      }
      this.context.resetStore()
      this.firstTabIsDirty = false
      this.secondTabIsDirty = false
      this.thirdTabIsDirty = false
    },
    showUtmTagCheckBox() {
      return this.context.providersWithUtmTags.includes(this.store.provider)
    },
    rebuildUrl() {
      const provider = this.store.provider
      const programID = this.store.programID
      const accountId = this.store.providerAccountID
      const remoteId = this.storeProviders
        .find((storeProvider) => storeProvider.key === provider)
        .accounts.find((account) => account.id == accountId)?.remote_id

      // Only get utm tags with values, map them to build the code then join them to convert it to a string
      const utmTags = Object.values(this.context.utmTags)
        .filter((tag) => tag.value && tag.value !== '')
        .map((tag) => `${tag.code}=${tag.value}`)
        .join('&')

      switch (provider) {
        case 'tradedoubler':
          return this.context.updateField({
            field: 'url',
            value: utmTags
              ? `https://clk.tradedoubler.com/click?p(${programID})a(${remoteId})url(:TARGET_ENCODED_URL:DIVIDER${utmTags})`
              : `https://clk.tradedoubler.com/click?p(${programID})a(${remoteId})url(:TARGET_ENCODED_URL)`,
          })
        case 'awin':
          return this.context.updateField({
            field: 'url',
            value: utmTags
              ? `https://www.awin1.com/cread.php?awinmid=${programID}&awinaffid=${remoteId}&p=:TARGET_ENCODED_URL:DIVIDER${utmTags}`
              : `https://www.awin1.com/cread.php?awinmid=${programID}&awinaffid=${remoteId}&p=:TARGET_ENCODED_URL`,
          })
        case 'adtraction':
          return this.context.updateField({
            field: 'url',
            value: utmTags
              ? `https://track.adtraction.com/t/t?a=${programID}&as=${remoteId}&t=2&tk=1&url=:TARGET_ENCODED_URL:DIVIDER${utmTags}`
              : `https://track.adtraction.com/t/t?a=${programID}&as=${remoteId}&t=2&tk=1&url=:TARGET_ENCODED_URL`,
          })
        case 'webgains':
          return this.context.updateField({
            field: 'url',
            value: utmTags
              ? `https://track.webgains.com/click.html?wgcampaignid=${remoteId}&wgprogramid=${programID}&wgtarget=:TARGET_ENCODED_URL:DIVIDER${utmTags}`
              : `https://track.webgains.com/click.html?wgcampaignid=${remoteId}&wgprogramid=${programID}&wgtarget=:TARGET_ENCODED_URL`,
          })
        case 'ga':
          return this.context.updateField({
            field: 'url',
            value: utmTags ? `${this.defaultStoreUrl}&${utmTags}` : `${this.defaultStoreUrl}`,
          })
        default:
          return this.context.updateField({
            field: 'url',
            value: this.defaultStoreUrl,
          })
      }
    },
    isDueDaysValid(value) {
      return value >= 0 && value <= 90
    },
  },
  watch: {
    'store.programID'(newValue, old) {
      newValue !== old && this.rebuildUrl()
    },
    'store.providerAccountID'(newValue, old) {
      newValue !== old && this.rebuildUrl()
    },
    'store.provider'(newValue, old) {
      if (newValue !== old) {
        const placeHolder = this.context.getUrlPlaceholder(newValue)
        placeHolder && this.context.setUrlPlaceholder(placeHolder)
        this.rebuildUrl()
      }
    },
  },
}
